/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
// AUTO-GENERATED by scripts/generateUnitsJson.ts. Do not edit by hand.
import { Phenomena, type PhenomenonName, type UnitName, Units } from "../generated/Units.generated";

/** @internal */
export const defaultPersistenceUnits = {
  [Phenomena.ACCELERATION]: Units.ACCELERATION.M_PER_SEC_SQ,
  [Phenomena.ANGLE]: Units.ANGLE.RAD,
  [Phenomena.ANGULAR_VELOCITY]: Units.ANGULAR_VELOCITY.RAD_PER_SEC,
  [Phenomena.APPARENT_POWER]: Units.APPARENT_POWER.VA,
  [Phenomena.AREA]: Units.AREA.SQ_M,
  [Phenomena.AREA_MOMENT_INERTIA]: Units.AREA_MOMENT_INERTIA.M_TO_THE_FOURTH,
  [Phenomena.AREA_SPRING_CONSTANT]: Units.AREA_SPRING_CONSTANT.AREA_SPRING_CONSTANT_N_PER_CUB_M,
  [Phenomena.AREA_TORQUE]: Units.AREA_TORQUE.N_M_PER_SQ_M,
  [Phenomena.CAPITA]: Units.CAPITA.PERSON,
  [Phenomena.CURRENCY]: Units.CURRENCY.US_DOLLAR,
  [Phenomena.CURRENT]: Units.CURRENT.A,
  [Phenomena.DENSITY]: Units.DENSITY.KG_PER_CUB_M,
  [Phenomena.DYNAMIC_VISCOSITY]: Units.DYNAMIC_VISCOSITY.PA_S,
  [Phenomena.ELECTRIC_CHARGE]: Units.ELECTRIC_CHARGE.COULOMB,
  [Phenomena.ELECTRIC_POTENTIAL]: Units.ELECTRIC_POTENTIAL.VOLT,
  [Phenomena.ELECTRICAL_RESISTANCE]: Units.ELECTRICAL_RESISTANCE.OHM,
  [Phenomena.ELECTRICAL_RESISTIVITY]: Units.ELECTRICAL_RESISTIVITY.OHM_M,
  [Phenomena.ENERGY_DENSITY]: Units.ENERGY_DENSITY.J_PER_CUB_M,
  [Phenomena.ENTROPY]: Units.ENTROPY.J_PER_K,
  [Phenomena.FLOW_DENSITY_PER_AREA]: Units.FLOW_DENSITY_PER_AREA.CUB_M_PER_SQ_M_SEC,
  [Phenomena.FORCE]: Units.FORCE.N,
  [Phenomena.FORCE_DENSITY]: Units.FORCE_DENSITY.N_PER_CUB_M,
  [Phenomena.FORCE_DISPLAY_SCALE]: Units.FORCE_DISPLAY_SCALE.M_PER_N,
  [Phenomena.FREQUENCY]: Units.FREQUENCY.HZ,
  [Phenomena.HEAT_FLUX_DENSITY]: Units.HEAT_FLUX_DENSITY.W_PER_SQ_M,
  [Phenomena.HEAT_TRANSFER]: Units.HEAT_TRANSFER.W_PER_SQ_M_K,
  [Phenomena.HORIZONTAL_DIRECTION]: Units.HORIZONTAL_DIRECTION.HORIZONTAL_DIR_RAD,
  [Phenomena.ILLUMINANCE]: Units.ILLUMINANCE.LUX,
  [Phenomena.KINEMATIC_VISCOSITY]: Units.KINEMATIC_VISCOSITY.SQ_M_PER_SEC,
  [Phenomena.LENGTH]: Units.LENGTH.M,
  [Phenomena.LINEAR_COEFFICIENT_OF_THERMAL_EXPANSION]: Units.LINEAR_COEFFICIENT_OF_THERMAL_EXPANSION.STRAIN_PER_KELVIN,
  [Phenomena.LINEAR_DENSITY]: Units.LINEAR_DENSITY.KG_PER_M,
  [Phenomena.LINEAR_LOAD]: Units.LINEAR_LOAD.N_PER_M,
  [Phenomena.LINEAR_RATE]: Units.LINEAR_RATE.PER_M,
  [Phenomena.LINEAR_ROTATIONAL_SPRING_CONSTANT]: Units.LINEAR_ROTATIONAL_SPRING_CONSTANT.N_PER_RAD,
  [Phenomena.LINEAR_SPRING_CONSTANT]: Units.LINEAR_SPRING_CONSTANT.LINEAR_SPRING_CONSTANT_N_PER_SQ_M,
  [Phenomena.LINEAR_TORQUE]: Units.LINEAR_TORQUE.N_M_PER_M,
  [Phenomena.LUMINOSITY]: Units.LUMINOSITY.CD,
  [Phenomena.LUMINOUS_EFFICACY]: Units.LUMINOUS_EFFICACY.LUMEN_PER_W,
  [Phenomena.LUMINOUS_FLUX]: Units.LUMINOUS_FLUX.LUMEN,
  [Phenomena.MASS]: Units.MASS.KG,
  [Phenomena.MASS_FLOW]: Units.MASS_FLOW.KG_PER_SEC,
  [Phenomena.MASS_RATIO]: Units.MASS_RATIO.KG_PER_KG,
  [Phenomena.MOLAR_CONCENTRATION]: Units.MOLAR_CONCENTRATION.MOL_PER_CUB_M,
  [Phenomena.MOLAR_FLOW]: Units.MOLAR_FLOW.MOL_PER_SEC,
  [Phenomena.MOLAR_VOLUME]: Units.MOLAR_VOLUME.CUB_M_PER_MOL,
  [Phenomena.MOLE]: Units.MOLE.MOL,
  [Phenomena.MOMENT_DISPLAY_SCALE]: Units.MOMENT_DISPLAY_SCALE.INVERSE_N,
  [Phenomena.NUMBER]: Units.NUMBER.ONE,
  [Phenomena.PERCENTAGE]: Units.PERCENTAGE.DECIMAL_PERCENT,
  [Phenomena.PIPE_DIAMETER_LENGTH]: Units.PIPE_DIAMETER_LENGTH.M_M,
  [Phenomena.POPULATION_DENSITY]: Units.POPULATION_DENSITY.PERSON_PER_SQ_M,
  [Phenomena.POWER]: Units.POWER.W,
  [Phenomena.PRESSURE]: Units.PRESSURE.PA,
  [Phenomena.PRESSURE_GRADIENT]: Units.PRESSURE_GRADIENT.PA_PER_M,
  [Phenomena.PROBABILITY]: Units.PROBABILITY.PROBABILITY_FRACTION,
  [Phenomena.ROTATIONAL_SPRING_CONSTANT]: Units.ROTATIONAL_SPRING_CONSTANT.N_M_PER_RAD,
  [Phenomena.SLOPE]: Units.SLOPE.M_PER_M,
  [Phenomena.SOLIDANGLE]: Units.SOLIDANGLE.STERAD,
  [Phenomena.SPECIFIC_ENERGY]: Units.SPECIFIC_ENERGY.J_PER_KG,
  [Phenomena.SPECIFIC_HEAT_CAPACITY]: Units.SPECIFIC_HEAT_CAPACITY.J_PER_KG_K,
  [Phenomena.SPECIFIC_HEAT_CAPACITY_MOLAR]: Units.SPECIFIC_HEAT_CAPACITY_MOLAR.J_PER_MOL_K,
  [Phenomena.SPECIFIC_VOLUME]: Units.SPECIFIC_VOLUME.CUB_M_PER_KG,
  [Phenomena.SPRING_CONSTANT]: Units.SPRING_CONSTANT.SPRING_CONSTANT_N_PER_M,
  [Phenomena.SURFACE_DENSITY]: Units.SURFACE_DENSITY.KG_PER_SQ_M,
  [Phenomena.TEMPERATURE]: Units.TEMPERATURE.K,
  [Phenomena.TEMPERATURE_CHANGE]: Units.TEMPERATURE_CHANGE.DELTA_KELVIN,
  [Phenomena.TEMPERATURE_GRADIENT]: Units.TEMPERATURE_GRADIENT.KELVIN_PER_M,
  [Phenomena.THERMAL_CONDUCTIVITY]: Units.THERMAL_CONDUCTIVITY.W_PER_M_K,
  [Phenomena.THERMAL_INSULANCE]: Units.THERMAL_INSULANCE.SQ_M_KELVIN_PER_WATT,
  [Phenomena.THREAD_PITCH]: Units.THREAD_PITCH.M_PER_RAD,
  [Phenomena.TIME]: Units.TIME.S,
  [Phenomena.TORQUE]: Units.TORQUE.N_M,
  [Phenomena.TORSIONAL_WARPING_CONSTANT]: Units.TORSIONAL_WARPING_CONSTANT.M_TO_THE_SIXTH,
  [Phenomena.VELOCITY]: Units.VELOCITY.M_PER_SEC,
  [Phenomena.VOLUME]: Units.VOLUME.CUB_M,
  [Phenomena.VOLUME_PER_LENGTH]: Units.VOLUME_PER_LENGTH.CUB_M_PER_M,
  [Phenomena.VOLUME_RATIO]: Units.VOLUME_RATIO.CUB_M_PER_CUB_M,
  [Phenomena.VOLUMETRIC_FLOW]: Units.VOLUMETRIC_FLOW.CUB_M_PER_SEC,
  [Phenomena.WORK]: Units.WORK.J,
  // Phenomena.LENGTH_RATIO is intentionally omitted because the bundled built-in unit set does not yet provide an agreed default for that phenomenon.
} as const satisfies Record<Exclude<PhenomenonName, typeof Phenomena.LENGTH_RATIO>, UnitName>;
